/**
 * La lista de conversaciones (§15.3). Los filtros viven en la URL: acá se leen para los vacíos, los links y
 * la paginación, y los maneja `ConversacionesLayout`.
 *
 * El número va ENTERO y siempre visible (decisión de Alan, R3): nunca `destinoParaMostrar`, nunca adentro de
 * un elemento que lo trunque. Con un nombre largo y cuatro chips, un `truncate` lo cortaba y el operador
 * no sabía a quién le estaba por escribir.
 *
 * Cada fila copia a su link SÓLO los filtros de la lista (`hrefConversacion`): copiar `location.search`
 * entero pasaba `?accion=reabrir` a la conversación siguiente y le abría la reapertura a otra persona.
 */

import { useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessagesSquare } from "lucide-react";
import type { ConversacionFila, PaginaConversaciones } from "../../types.conversaciones";
import { CajaError, Esqueleto, haceCuanto } from "../base/comun";
import {
  ESTADO_CONVERSACION, faltaPoco, filtroDeUrl, hrefConversacion, textoVentanaCorto,
} from "../../logic/conversaciones";

const chip = "text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap";

/**
 * Lleva el principio de la lista a la vista moviendo sólo los contenedores con scroll que lo tapan, y sólo lo
 * necesario. Cambiar `?page=` no cambia el pathname: DashboardLayout no sube, y el operador aterrizaba en las
 * últimas filas de la página nueva. No es un `scrollIntoView`: ése alinea también el contenedor de la página, y
 * desde `xl` (la lista en su columna con scroll propio) hacía saltar la página entera con la conversación al lado.
 */
function mostrarPrincipio(el: HTMLElement | null): void {
  for (let p = el?.parentElement; el && p && p !== document.body; p = p.parentElement) {
    if (p.scrollHeight <= p.clientHeight) continue;
    const arriba = el.getBoundingClientRect().top - p.getBoundingClientRect().top;
    if (arriba < 0) p.scrollTop += arriba;
  }
}

const VACIO: Record<string, string> = {
  sin_responder: "No hay conversaciones sin responder.",
  ventana_abierta: "Nadie escribió en las últimas 24 h.",
  todas: "Todavía no escribió nadie. Cuando alguien conteste una campaña o le escriba al número, aparece acá.",
  resueltas: "No hay conversaciones resueltas.",
  bajas: "Nadie pidió la baja por chat.",
};

export function ListaConversaciones({ pagina, cargando, error, onReintentar, detenido, onReintentarPolling, seleccionadaId, ahora }: {
  pagina: PaginaConversaciones | null;
  cargando: boolean;
  error: string;
  onReintentar: () => void;
  detenido: boolean;
  onReintentarPolling: () => void;
  seleccionadaId: string | null;
  ahora: Date;
}) {
  const [sp, setSp] = useSearchParams();
  const { filtro, q, page } = filtroDeUrl(sp);
  const inicio = useRef<HTMLElement>(null);

  const irAPagina = (p: number) => {
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      if (p > 1) n.set("page", String(p)); else n.delete("page");
      return n;
    });
    mostrarPrincipio(inicio.current);
  };

  // «Ver todas» cambia SÓLO el filtro, como el chip «Todas»: un link a la lista sin el `:id` cerraba la conversación
  // abierta al lado (y perdía `campana`). Sin pathname, el link se queda en la ruta actual.
  const verTodas = new URLSearchParams(sp);
  verTodas.set("filtro", "todas");
  verTodas.delete("page");

  const total = pagina?.total ?? 0;
  const limit = pagina?.limit || 50;
  // Pasadas las 1.000 el backend deja de contar (la lista se pide cada 20 s por operador) y `total` es un piso: no se
  // sabe cuántas páginas hay. Ahí hay una siguiente mientras la de ahora venga llena; con la cuenta de `total` la lista
  // se quedaba en «Página 20 de 20» aunque hubiera 30.000 conversaciones.
  const alTope = !!pagina?.totalAlTope;
  const paginas = Math.max(1, Math.ceil(total / limit));
  const haySiguiente = alTope ? (pagina?.items.length ?? 0) >= limit : page < paginas;
  const pendientes = pagina?.contador?.pendientesDeLlenado ?? 0;

  return (
    <section ref={inicio} data-testid="lista-conversaciones" className="overflow-hidden rounded-2xl border border-slate-100 bg-white p-4 sm:p-6 shadow-sm">
      {/* El título «Conversaciones» y su bajada son de la página (`ConversacionesLayout`), arriba de los filtros. */}
      {detenido && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <span>Dejamos de actualizar la lista porque el servidor no responde.</span>
          <button type="button" onClick={onReintentarPolling} className="font-semibold underline">Reintentar</button>
        </div>
      )}

      {cargando && !pagina ? (
        <Esqueleto filas={5} alto="h-16" />
      ) : error && !pagina ? (
        <CajaError titulo="No pudimos traer las conversaciones." detalle={error} onReintentar={onReintentar} />
      ) : !pagina || !pagina.items.length ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center">
          <MessagesSquare size={22} className="mx-auto text-slate-300" />
          {pendientes > 0 ? (
            <p className="mt-3 text-sm text-slate-600">Estamos cargando las conversaciones anteriores (quedan {pendientes}).</p>
          ) : total > 0 ? (
            // Una `?page=N` que ya no existe (se resolvieron varias mientras tanto): no es «no hay nadie».
            <>
              <p className="mt-3 text-sm text-slate-600">No hay más conversaciones en esta página.</p>
              <button type="button" onClick={() => irAPagina(1)} className="mt-2 text-sm font-semibold text-violet-700 underline">Ir a la primera página</button>
            </>
          ) : q ? (
            <p className="mt-3 text-sm text-slate-600">No hay conversaciones que coincidan con «{q}».</p>
          ) : filtro === "por_contestar" ? (
            <>
              <p className="mt-3 text-sm text-slate-600">No hay nadie esperando respuesta.</p>
              <Link to={{ search: `?${verTodas}` }} className="mt-2 inline-block text-sm font-semibold text-violet-700 underline">Ver todas</Link>
            </>
          ) : (
            <p className="mt-3 text-sm text-slate-600">{VACIO[filtro]}</p>
          )}
        </div>
      ) : (
        <>
          {/* Mientras llega otra página (o otro filtro) siguen las filas de antes: se atenúan para que no se lean como las nuevas.
              Sin el título adentro, las filas van contra el borde de la tarjeta (arriba si no hay aviso, abajo si no hay
              páginas): si no, quedaba una franja vacía donde estaba el título. */}
          <ul aria-busy={cargando || undefined}
            className={`-mx-4 sm:-mx-6 divide-y divide-slate-100 border-y border-slate-100 first:-mt-4 sm:first:-mt-6 first:border-t-0 last:-mb-4 sm:last:-mb-6 last:border-b-0 transition-opacity ${cargando ? "opacity-60" : ""}`}>
            {pagina.items.map((c) => (
              <Fila key={c.id} c={c} href={hrefConversacion(c.id, sp)} seleccionada={c.id === seleccionadaId} ahora={ahora} />
            ))}
          </ul>
          {(paginas > 1 || alTope) && (
            <nav aria-label="Páginas de conversaciones" className="mt-4 flex items-center justify-between gap-3 text-sm">
              <button type="button" onClick={() => irAPagina(page - 1)} disabled={page <= 1}
                className="font-semibold text-violet-700 disabled:text-slate-300">Anteriores</button>
              <span className="text-slate-500">
                {alTope ? `Página ${page} · ${total.toLocaleString("es-AR")}+ conversaciones` : `Página ${page} de ${paginas}`}
              </span>
              <button type="button" onClick={() => irAPagina(page + 1)} disabled={!haySiguiente}
                className="font-semibold text-violet-700 disabled:text-slate-300">Siguientes</button>
            </nav>
          )}
        </>
      )}
    </section>
  );
}

function Fila({ c, href, seleccionada, ahora }: { c: ConversacionFila; href: string; seleccionada: boolean; ahora: Date }) {
  const estado = ESTADO_CONVERSACION[c.estado] ?? { texto: String(c.estado || "sin estado"), cls: "bg-slate-100 text-slate-500" };
  const abierta = c.ventanaAbierta && Date.parse(c.contestarHasta) > ahora.getTime();
  const numero = c.numero ? (
    <span className="font-mono whitespace-nowrap shrink-0" data-testid="numero-conversacion">{c.numero}</span>
  ) : null;

  return (
    <li>
      <Link to={href} aria-current={seleccionada ? "page" : undefined}
        className={`block px-4 sm:px-6 py-3 transition-colors ${seleccionada ? "bg-violet-50" : "hover:bg-slate-50"}`}>
        <div className="flex items-start justify-between gap-3">
          {c.nombre ? (
            <p className="min-w-0 truncate text-sm font-semibold text-slate-900">{c.nombre}</p>
          ) : numero ? (
            <p className="text-sm font-semibold text-slate-900">{numero}</p>
          ) : (
            <p className="text-sm font-semibold text-slate-900">Sin número (usuario de WhatsApp)</p>
          )}
          <span className="shrink-0 whitespace-nowrap text-xs text-slate-400">{haceCuanto(c.ultimoEntranteAt, ahora)}</span>
        </div>
        {c.nombre && (numero ? (
          <p className="text-xs text-slate-600">{numero}</p>
        ) : (
          <p className="text-xs text-slate-500">Sin número (usuario de WhatsApp)</p>
        ))}
        {c.vistaPrevia && <p className="mt-1 truncate text-sm text-slate-600">{c.vistaPrevia}</p>}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span className={`${chip} ${estado.cls}`}>{estado.texto}</span>
          {abierta && (
            <span className={`${chip} ${faltaPoco(c.contestarHasta, ahora) ? "bg-amber-50 text-amber-700" : "bg-violet-50 text-violet-700"}`}>
              Ventana abierta hasta {textoVentanaCorto(c.contestarHasta, ahora)}
            </span>
          )}
          {c.campana && <span className={`${chip} bg-slate-100 text-slate-600 normal-case tracking-normal`}>{c.campana.nombre}</span>}
          {c.simulado && <span className={`${chip} bg-amber-50 text-amber-700`}>simulada</span>}
        </div>
      </Link>
    </li>
  );
}

export default ListaConversaciones;
